import React from 'react';
import {
    Alert, Button, Card, Col, Input, InputNumber, Progress, Row, Select,
    Space, Table, Tag, Typography, message, AutoComplete, Segmented
} from 'antd';
import { RadarChartOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import RemedyEditor, { queryLanguageFor, normalizeField } from '../util/monaco-remedy';
import { loadFieldsForForm, getConnectorForms, normalizeConnector } from '../util/redux/anonymizerSlice';
import { anonApi } from '../util/anonymizerApi';

const { Text } = Typography;

/**
 * PII Discovery: scan a source system to find WHERE personal data lives -
 * seed values (e.g. a test person from CTM:People) are searched across all
 * sampled columns, and free-text columns are ML-scanned for PII entities.
 */
const DiscoveryPanel = () => {
    const dispatch = useDispatch();
    const { connectors, forms, formsByConnector, fields } = useSelector((state) => state.anonymizer);

    const [connector, setConnector] = React.useState('remedy');
    const [filter, setFilter] = React.useState('');
    const [maxCollections, setMaxCollections] = React.useState(20);
    const [sampleSize, setSampleSize] = React.useState(5);
    const [scanMode, setScanMode] = React.useState('both');
    const [seedConnector, setSeedConnector] = React.useState('remedy');
    const [seedCollection, setSeedCollection] = React.useState('');
    const [seedQuery, setSeedQuery] = React.useState('');
    const [seedFields, setSeedFields] = React.useState([]);
    const [seedMaxRecords, setSeedMaxRecords] = React.useState(50);
    const [starting, setStarting] = React.useState(false);
    const [run, setRun] = React.useState(null);

    // latest run on mount, then poll while running
    React.useEffect(() => {
        anonApi('/v1/discovery', { method: 'GET' })
            .then((runs) => setRun(runs && runs[0] ? runs[0] : null))
            .catch(() => {});
    }, []);

    React.useEffect(() => {
        if (!run || run.status !== 'running') return undefined;
        const timer = setInterval(() => {
            anonApi(`/v1/discovery/${run.id}`, { method: 'GET' })
                .then(setRun)
                .catch(() => {});
        }, 3000);
        return () => clearInterval(timer);
    }, [run]);

    const start = () => {
        if (scanMode !== 'ml' && !seedCollection) {
            message.error('Seed matching needs a seed collection - or switch to "ML scan only"');
            return;
        }
        setStarting(true);
        anonApi('/v1/discovery', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                connector,
                collectionFilter: filter || undefined,
                maxCollections,
                sampleSize,
                mlScan: scanMode !== 'seed',
                seed: (scanMode !== 'ml' && seedCollection)
                    ? { connector: seedConnector, collection: seedCollection, query: seedQuery, fields: seedFields, maxRecords: seedMaxRecords }
                    : undefined
            })
        })
            .then(setRun)
            .catch((e) => message.error('Start failed: ' + e.message))
            .finally(() => setStarting(false));
    };

    const running = run && run.status === 'running';
    const findings = (run && run.findings) || [];

    // the seed can come from a DIFFERENT system than the one being scanned
    // (e.g. person data from Remedy, searched inside Innovation Studio)
    const seedCollectionOptions = seedConnector === 'remedy'
        ? (forms || [])
        : ((formsByConnector || {})[seedConnector] || []);
    const seedFieldMeta = (fields || []).find(
        (e) => e.form === seedCollection && normalizeConnector(e.connector) === normalizeConnector(seedConnector === 'remedy' ? undefined : seedConnector)
    );
    const seedFieldList = (seedFieldMeta && seedFieldMeta.fields) || [];
    const ql = queryLanguageFor(connectors, seedConnector === 'remedy' ? undefined : seedConnector);

    const onConnectorChange = (c) => {
        setConnector(c);
        if (c !== 'remedy' && !(formsByConnector || {})[c]) {
            dispatch(getConnectorForms(c));
        }
    };

    const onSeedConnectorChange = (c) => {
        setSeedConnector(c);
        setSeedCollection('');
        setSeedFields([]);
        if (c !== 'remedy' && !(formsByConnector || {})[c]) {
            dispatch(getConnectorForms(c));
        }
    };

    const onSeedCollectionChange = (form) => {
        setSeedCollection(form);
        setSeedFields([]);
        if (form) {
            dispatch(loadFieldsForForm({ form, connector: seedConnector === 'remedy' ? undefined : seedConnector }));
        }
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card title="PII Discovery" size="small">
                <Row gutter={16}>
                    <Col span={6}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Source system</div>
                        <Select
                            value={connector}
                            onChange={onConnectorChange}
                            style={{ width: '100%' }}
                            options={(connectors || []).map((c) => ({ value: c.name, label: c.displayName || c.name }))}
                        />
                    </Col>
                    <Col span={8}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Collection filter (contains)</div>
                        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder='e.g. "HPD:" - empty = all' />
                    </Col>
                    <Col span={5}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Max collections</div>
                        <InputNumber min={1} max={200} value={maxCollections} onChange={setMaxCollections} style={{ width: '100%' }} />
                    </Col>
                    <Col span={5}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Records per collection</div>
                        <InputNumber min={1} max={50} value={sampleSize} onChange={setSampleSize} style={{ width: '100%' }} />
                    </Col>
                </Row>
                <div style={{ marginTop: 12 }}>
                    <Segmented
                        value={scanMode}
                        onChange={setScanMode}
                        options={[
                            { value: 'both', label: 'Seed + ML scan' },
                            { value: 'seed', label: 'Seed only' },
                            { value: 'ml', label: 'ML scan only' }
                        ]}
                    />
                </div>
                {scanMode !== 'ml' && (
                <Row gutter={16} style={{ marginTop: 12 }}>
                    <Col span={4}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Seed source</div>
                        <Select
                            value={seedConnector}
                            onChange={onSeedConnectorChange}
                            style={{ width: '100%' }}
                            options={(connectors || []).map((c) => ({ value: c.name, label: c.displayName || c.name }))}
                        />
                    </Col>
                    <Col span={5}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Seed collection (optional)</div>
                        <AutoComplete
                            value={seedCollection}
                            onChange={onSeedCollectionChange}
                            options={seedCollectionOptions.map((f) => ({ value: f }))}
                            filterOption={(input, o) => o.value.toLowerCase().includes(input.toLowerCase())}
                            style={{ width: '100%' }}
                            placeholder="e.g. CTM:People"
                        />
                    </Col>
                    <Col span={8}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                            Seed query ({ql.name})
                        </div>
                        <RemedyEditor
                            key={'discovery-seed-' + seedConnector + '-' + seedCollection}
                            index={'discovery-seed-' + seedConnector + '-' + seedCollection}
                            height={'60px'}
                            minimap={{ enabled: false }}
                            defaultValue={seedQuery}
                            formFields={seedFieldList}
                            form={seedCollection || 'discovery'}
                            queryLanguage={ql}
                            onChange={setSeedQuery}
                        />
                    </Col>
                    <Col span={5}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Seed fields</div>
                        <Select
                            mode="tags"
                            value={seedFields}
                            onChange={setSeedFields}
                            style={{ width: '100%' }}
                            placeholder="e.g. Full Name, Internet E-mail"
                            options={seedFieldList.map((f) => {
                                const nf = normalizeField(f);
                                return { value: nf.name, label: nf.description ? `${nf.name} — ${nf.description}` : nf.name };
                            })}
                            tokenSeparators={[',']}
                        />
                    </Col>
                    <Col span={2}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Max records</div>
                        <InputNumber min={1} max={500} value={seedMaxRecords} onChange={setSeedMaxRecords} style={{ width: '100%' }} />
                    </Col>
                </Row>
                )}
                <Space style={{ marginTop: 16 }}>
                    <Button type="primary" icon={<RadarChartOutlined />} loading={starting || running} onClick={start} disabled={running}>
                        {running ? 'Scanning…' : 'Start Discovery'}
                    </Button>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Seed values (e.g. a test person) are searched in every sampled column; free-text
                        columns are ML-scanned for PII. Read-only - nothing is changed.
                    </Text>
                </Space>
                {running && run.progress && run.progress.total > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <Progress percent={Math.round((run.progress.done / run.progress.total) * 100)} />
                        {run.progress.current && <Text type="secondary" style={{ fontSize: 12 }}>scanning: {run.progress.current}</Text>}
                    </div>
                )}
            </Card>

            {run && run.status !== 'running' && (
                <Card
                    size="small"
                    title={
                        <Space>
                            Results
                            <Tag color={run.status === 'finished' ? 'success' : run.status === 'failed' ? 'error' : 'warning'}>{run.status}</Tag>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {run.scannedCollections} collections · {run.scannedRecords} records sampled · {run.mlCalls} ML scans
                                {run.params && run.params.seed && ` · ${run.seedValues || 0} seed values`}
                                {run.collectionsTruncated > 0 && ` · ${run.collectionsTruncated} collections skipped (raise max)`}
                            </Text>
                        </Space>
                    }
                >
                    {run.params && run.params.seed && !run.seedValues && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="Seed query returned no values"
                            description="The seed collection/query did not yield any usable field values - seed matching was effectively off for this scan. Check the query and seed fields."
                        />
                    )}
                    {(run.errors || []).length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={`${run.errors.length} collection(s) could not be scanned`}
                            description={run.errors.slice(0, 5).map((e, i) => (
                                <div key={i}>{e.collection ? `${e.collection}: ` : ''}{e.message}</div>
                            ))}
                        />
                    )}
                    <Table
                        size="small"
                        rowKey={(r) => r.collection + '|' + r.field}
                        dataSource={findings}
                        pagination={{ pageSize: 15 }}
                        columns={[
                            { title: 'Collection', dataIndex: 'collection', width: 220, render: (v) => <b>{v}</b> },
                            { title: 'Field', dataIndex: 'field', width: 200 },
                            {
                                title: 'Seed matches',
                                key: 'seed',
                                width: 220,
                                render: (_, r) => Object.keys(r.seedMatches || {}).map((k) => (
                                    <Tag key={k} color="magenta">{k} ×{r.seedMatches[k]}</Tag>
                                ))
                            },
                            {
                                title: 'Detected PII',
                                key: 'ents',
                                width: 240,
                                render: (_, r) => Object.keys(r.entities || {}).map((k) => (
                                    <Tag key={k} color="geekblue">{k} ×{r.entities[k]}</Tag>
                                ))
                            },
                            {
                                title: 'Sample',
                                key: 'sample',
                                render: (_, r) => <Text type="secondary" style={{ fontSize: 12 }}>{(r.samples || [])[0]}</Text>
                            }
                        ]}
                    />
                </Card>
            )}
        </Space>
    );
};

export default DiscoveryPanel;
